import { useEffect, useMemo, useState } from "react";
import { Download, Share, X } from "lucide-react";
import i18n from "@/i18n";

const copy = {
  ca: { title: "Instal·la Matchium", body: "Accedeix als teus tornejos com una aplicació.", action: "Instal·la", ios: "Prem Compartir i després «Afegeix a la pantalla d'inici».", close: "Ara no" },
  es: { title: "Instala Matchium", body: "Accede a tus torneos como una aplicación.", action: "Instalar", ios: "Pulsa Compartir y después «Añadir a pantalla de inicio».", close: "Ahora no" },
  en: { title: "Install Matchium", body: "Access your tournaments like an app.", action: "Install", ios: "Tap Share, then “Add to Home Screen”.", close: "Not now" },
  fr: { title: "Installer Matchium", body: "Accédez à vos tournois comme dans une application.", action: "Installer", ios: "Touchez Partager, puis « Sur l’écran d’accueil ».", close: "Plus tard" },
  pt: { title: "Instalar o Matchium", body: "Acede aos teus torneios como uma aplicação.", action: "Instalar", ios: "Toca em Partilhar e depois «Adicionar ao ecrã principal».", close: "Agora não" }
};

const DISMISS_KEY = "matchium-install-prompt-dismissed";

export default function InstallAppPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [visible, setVisible] = useState(false);
  const language = (i18n.resolvedLanguage || i18n.language || "ca").split("-")[0];
  const text = useMemo(() => copy[language] || copy.ca, [language]);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone || localStorage.getItem(DISMISS_KEY)) return undefined;

    const onPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isSafari = /safari/i.test(window.navigator.userAgent) && !/crios|fxios|edgios/i.test(window.navigator.userAgent);
    if (isIos && isSafari) {
      setShowIosHelp(true);
      setVisible(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-md rounded-2xl border border-border/70 bg-card/95 p-4 shadow-2xl backdrop-blur" role="dialog" aria-label={text.title} data-testid="install-app-prompt">
      <button type="button" onClick={dismiss} className="absolute right-3 top-3 rounded-lg p-1.5 text-muted-foreground hover:bg-muted" aria-label={text.close}>
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-3 pr-8">
        <img src="/pwa-icon-192.png" alt="" loading="lazy" decoding="async" className="h-12 w-12 rounded-xl" />
        <div>
          <p className="font-semibold text-foreground">{text.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{showIosHelp ? text.ios : text.body}</p>
        </div>
      </div>
      {installEvent && (
        <button type="button" onClick={install} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-semibold text-primary-foreground hover:bg-primary/90" data-testid="install-app-confirm">
          <Download className="h-4 w-4" /> {text.action}
        </button>
      )}
      {showIosHelp && <Share className="absolute bottom-5 right-5 h-4 w-4 text-primary" aria-hidden="true" />}
    </aside>
  );
}
